import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  ViewEncapsulation,
  signal,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef as MatDialogReference,
} from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { NGXLogger } from 'ngx-logger';
import { Observable, finalize, interval, map, take } from 'rxjs';

import { BulkUpdateProvider } from 'lib/models/src/public-api';
import {
  BulkUpdateJob,
  BulkUpdateStatus,
  Field,
  FieldDataType,
  ListType,
  Search,
  SearchOperator,
  SearchParameter,
  SearchPrompt,
} from 'models';
import { BULK_UPDATE_PROVIDER } from 'src/app/common/tokens';
import { LayoutService } from 'src/app/services/layout.service';
import { ArchivesQuery } from 'src/app/state/archives/archives.query';
import { DatabasesQuery } from 'src/app/state/databases/databases.query';
import { FieldsQuery } from 'src/app/state/fields/fields.query';
import { GridSettingsService } from 'src/app/state/grid/grid-states.service';
import { ListsService } from 'src/app/state/lists/lists.service';

import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationExtras, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { assertExists } from 'common';
import moment from 'moment';
import { encodeBase64 } from 'src/app/common/utility';
import { SupportedFeaturesService } from 'src/app/services/supported-features.service';
import { SearchesQuery } from 'src/app/state/searches/searches.query';
import { MultiValueEntryAddedEvent } from '../field-components/multi-value-field/multi-value-field.component';
import {
  SearchPromptComponent,
  SearchPromptDialogData,
  SearchPromptResult,
} from '../search-prompt/search-prompt.component';

/** Enum for tracking status. */
export enum Status {
  /** Cancel. */
  cancel,
  /** Complete. */
  complete,
  /** Waiting for confirmation. */
  confirm,
  /** Updating. */
  updating,
}

type ErrorInfo = {
  /** number of errored documents. */
  erroredNumber: number;
  /**
   * Array of error messages.
   *
   * Note that a {@link Set} is used when creating this array so the error messages are unique.
   */
  errorMessages: string[];
  /** Map of archive ids along with an array of any document ids that were errored. This is used for the opening errored documents. */
  erroredDocuments: Map<number, number[]>;
};

/** Describes data that can be passed to the bulk update dialog. */
export interface BulkUpdateDialogData {
  // Can define properties here if we need to add some.
  /**
   * Optionally provide a search which can include search parameters to pre-populate.
   */
  search?: Search;
  /** Overrides the default dialog title with the provided translation key. */
  alternateDialogTitleI18n?: string;
}

/** Bulk Document Component. */
@UntilDestroy()
@Component({
  selector: 'app-bulk-update-dialog',
  templateUrl: './bulk-update-dialog.component.html',
  styleUrls: ['./bulk-update-dialog.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class BulkUpdateDialogComponent implements OnInit {
  stepper = viewChild<MatStepper>('stepper');
  /** Current archive. */
  archive = this.archivesQuery.getActive();
  /** Archive fields. */
  archiveFields: Field[] = [];
  /** Current document being updated. */
  currentDocument = 0;
  /** Database Id. */
  databaseId = this.databaseQuery.activeId;
  /** Dialog title. */
  dialogTitle = signal('BULK_UPDATE');
  /** Error information. */
  errorInfo: ErrorInfo = {
    erroredNumber: 0,
    errorMessages: [],
    erroredDocuments: new Map<number, number[]>(),
  };
  /** Valid field list types. */
  fieldListType = ListType;
  /**
   * Observable array of fields.
   *
   * This excludes version fields.
   */
  fields$ = this.fieldsQuery.fields$.pipe(
    map((fields) =>
      fields.filter(
        (f) =>
          f.systemField !== 'documentVersion' &&
          f.systemField !== 'documentVersionParent' &&
          !f.liveField
      )
    )
  );
  /** Use compact observable. */
  isHandset$ = this.layout.isHandset$;
  /** Where the search is static or not. */
  isSearchStatic = false;
  /** Current job Id. */
  jobId = '';
  /** Whether the job is in the process of initializing.
   *
   * Job initialization can take some time because it must run a search.
   */
  jobIsInitializing = signal(false);
  /** List values. */
  listValues: { [listId: number]: Observable<string[]> } = {};
  /** List of non archive fields. */
  nonArchiveFields: Field[] = [];
  /** Overwrite system fields. */
  overwriteSystemFields = false;
  /** Progress. */
  progress = 0;
  /** List of parameters in a search. */
  searchParameters: SearchParameter[] = [];
  /** Search Setup Form Group. */
  searchSetupFormGroup: UntypedFormGroup;
  /** Searches. */
  searches$ = this.searchesQuery.all$.pipe(
    map((searches) =>
      searches.filter(
        (s) =>
          !s.archives.some(
            (a) => this.archivesQuery.getArchive(a).isCheckInCheckOut
          )
      )
    )
  );
  /** The Selected Search. */
  selectedSearch: Search;
  /** Replacements Form Group. */
  setReplacementsFormGroup: UntypedFormGroup;
  /** Show the search parameters. */
  showSearchParameters = false;
  /** Track update status. */
  status = Status.confirm;
  /** Whether the open errored documents button is supported. */
  supportedOpenErrored = toSignal(
    this.supportedFeatures.versionSupports$(
      this.supportedFeatures.bulkUpdateOpenErroredDocuments
    ),
    { initialValue: false }
  );
  /** Number of documents to be updated. */
  totalDocuments = 0;
  /** Polling interval in milliseconds for job status updates. */
  private readonly pollingIntervalMS = 5000;

  constructor(
    private logger: NGXLogger,
    @Inject(BULK_UPDATE_PROVIDER)
    private bulkUpdateProvider: BulkUpdateProvider,
    private layout: LayoutService,
    private archivesQuery: ArchivesQuery,
    private databaseQuery: DatabasesQuery,
    private fieldsQuery: FieldsQuery,
    private searchesQuery: SearchesQuery,
    private formBuilder: UntypedFormBuilder,
    private grid: GridSettingsService,
    private supportedFeatures: SupportedFeaturesService,
    private translateService: TranslocoService,
    private listsService: ListsService,
    private router: Router,
    public dialogReference: MatDialogReference<BulkUpdateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: BulkUpdateDialogData,
    private dialog: MatDialog
  ) {}
  /**
   * Get status enum.
   *
   * @returns - Status enum.
   */
  get statusEnums(): typeof Status {
    return Status;
  }
  /** Cancels the job by job id. */
  clickCancel() {
    this.bulkUpdateProvider
      .cancelJob(this.jobId)
      .pipe(untilDestroyed(this))
      .subscribe({
        error: (error) => {
          this.logger.error('Failed to cancel job', error);
        },
      });
  }
  /**
   * Display the search dialog.
   *
   */
  clickConfigureSearch() {
    // Remove the keyword search parameter if the user tries to configure search criteria.
    // We never want the user to interact with keyword search unless they are coming in with a pre-populated search that includes it
    // because the user has seen the search result in the grid.
    const keywordParameterIndex = this.searchParameters.findIndex(
      (p) => p.id === -1
    );
    if (keywordParameterIndex !== -1) {
      this.searchParameters.splice(keywordParameterIndex, 1);
    }
    const promptDialogData: SearchPromptDialogData = {
      /** Search. */
      search: this.selectedSearch,
      searchPrompts: this.searchParameters,
      hideContentSearch: true,
    };
    const promptDialog = this.dialog.open(SearchPromptComponent, {
      data: promptDialogData,
      minWidth: 500,
    });
    promptDialog
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result: SearchPromptResult) => {
        if (!result) {
          this.logger.debug('Search prompt dialog cancelled.');
          return;
        }
        this.setSearchPrompts(result.prompts);
        this.logger.debug('Search prompt closed', result);
      });
  }

  /** Click next. */
  clickNext(stepper: MatStepper) {
    stepper.next();
  }
  /** Click Back */
  clickBack(stepper: MatStepper) {
    stepper.previous();
  }
  /** Close dialog. */
  close() {
    if (this.jobId === '') {
      this.dialogReference.close();
    } else {
      this.bulkUpdateProvider
        .deleteJob(this.jobId)
        .pipe(
          untilDestroyed(this),
          finalize(() => {
            this.grid.refreshArchiveGrid(
              this.databaseId,
              this.archive?.id || 0
            );
            this.dialogReference.close();
          })
        )
        .subscribe({
          error: (err) => {
            this.logger.error('Failed to delete job: ', err);
          },
        });
    }
  }
  /**
   * Executes when the mat stepper changes.
   *
   * @param event - Stepper Selection Event
   */
  onStepSelectionChange(event: StepperSelectionEvent) {
    // If leaving the last tab.
    if (event.previouslySelectedIndex === 2) {
      this.deleteJob();
    }
    // If Entering the last tab
    else if (
      (event.previouslySelectedIndex === 0 ||
        event.previouslySelectedIndex === 1) &&
      event.selectedIndex === 2
    ) {
      this.initializeBulkSearchUpdate();
    } else if (event.selectedIndex === 1) {
      this.createFieldLists();
    }
  }
  /**
   * Creates archive field array.
   *
   * @param fields - Array of fields.
   * @param selectedArchiveFieldIds - Array of field ids that exist in the archives of searches.
   */
  createArchiveFieldArray(
    fields: Field[],
    selectedArchiveFieldIds: Set<number>
  ): void {
    // Get all fields that have and id that matches an id in selectedArchiveFieldIds.
    const archiveFields = fields
      .filter((field) => selectedArchiveFieldIds.has(field.id))
      .sort((a, b) => {
        if (a.name.toLowerCase() < b.name.toLowerCase()) {
          return -1;
        }
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        }
        return 0;
      });

    // Add all fields that have a matching id in archiveFields.
    this.archiveFields.push(
      ...archiveFields.filter(
        (field) => !this.archiveFields.some((f) => f.id === field.id)
      )
    );
  }
  /**
   * Create archive lists.
   *
   * @param fields - Array of Fields.
   */
  createArchiveLists(fields: Field[]): void {
    // Get all field ids for each archive in the search.
    const selectedArchiveFieldIds = new Set(
      this.selectedSearch.archives.flatMap(
        (archive) => this.archivesQuery.getArchive(archive).fieldIds
      )
    );
    this.createArchiveFieldArray(fields, selectedArchiveFieldIds);
    this.createNonArchiveArray(fields, selectedArchiveFieldIds);
  }
  /**
   * Create the field controls.
   *
   * @param fields - Fields array.
   */
  createFieldControls(fields: Field[]) {
    const controls: {
      [key: string]: AbstractControl<any, any>;
    } = {};
    for (const field of fields) {
      const control = new UntypedFormControl('');
      if (field.multiValue) {
        const mvControls = [control];
        controls[field.id.toString()] = this.formBuilder.array(mvControls);
      } else {
        controls[field.id.toString()] = control;
      }
    }
    this.setReplacementsFormGroup = this.formBuilder.group(controls);
    this.setReplacementsFormGroup.markAllAsTouched();
  }
  /**
   * Creates field lists.
   */
  createFieldLists() {
    this.archiveFields = [];
    this.nonArchiveFields = [];
    this.fields$
      .pipe(take(1), untilDestroyed(this))
      .subscribe((fields: Field[]) => {
        // get all table fields that are in any of the archives being searched.
        const tableFieldIdsToRemove = this.selectedSearch.archives
          .flatMap((archiveId) =>
            this.archivesQuery
              .getTableFields(archiveId)
              .flatMap((tf) => tf.fieldIds)
          )
          // Remove any possible duplicate field ids that may exist if multiple table fields share the same fields.
          .filter((id, index, self) => self.indexOf(id) === index);
        /** Remove tablefield fields from the fields list. */
        const filteredFields = fields.filter(
          (field) => !tableFieldIdsToRemove.includes(field.id)
        );
        this.createFieldControls(filteredFields);
        this.createArchiveLists(filteredFields);
        const mvSystemFields = fields.filter(
          (f) => f.multiValue && f.systemField
        );
        if (mvSystemFields.length > 0) {
          // Timeout to ensure underlying field component is created before setting enabled state.
          setTimeout(() => {
            for (const field of mvSystemFields) {
              const control = this.setReplacementsFormGroup.get(
                field.id.toString()
              );
              assertExists(control, 'Control must exist.');
              this.setSystemFieldControlEnabled(field, control);
            }
          });
        }
      });
  }
  /** Get all the data for all the possible dropdown lists. */
  createLists(): void {
    this.fields$
      .pipe(take(1), untilDestroyed(this))
      .subscribe((fields: Field[]) => {
        for (const field of fields) {
          const listId = field.list.listId;
          if (listId > 0) {
            this.listValues[listId] = this.getListValues(field.list.listId);
          }
        }
      });
  }
  /**
   * Creates archive field array.
   *
   * @param fields - Array of fields.
   * @param selectedArchiveFieldIds - Array of field ids that exist in the archives of searches.
   */
  createNonArchiveArray(
    fields: Field[],
    selectedArchiveFieldIds: Set<number>
  ): void {
    // Get all fields that DO NOT have and id that matches an id in selectedArchiveFieldIds.
    const nonArchiveFields = fields
      .filter((field) => !selectedArchiveFieldIds.has(field.id))
      .sort((a, b) => {
        if (a.name.toLowerCase() < b.name.toLowerCase()) {
          return -1;
        }
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        }
        return 0;
      });

    // Add all fields that have a matching id in nonArchiveFields.
    this.nonArchiveFields.push(
      ...nonArchiveFields.filter(
        (field) => !this.archiveFields.some((f) => f.id === field.id)
      )
    );
  }
  /**
   * Creates the body for bulk search update.
   *
   * @returns - UpdateData.
   */
  createPostBody() {
    const criteria = this.createCriteria();
    const updates = this.createUpdates();
    const mvUpdates = this.createMVUpdates();
    const overwriteSystemFields = this.overwriteSystemFields;
    return { criteria, updates, mvUpdates, overwriteSystemFields };
  }
  /** Delete job. */
  deleteJob() {
    this.bulkUpdateProvider
      .deleteJob(this.jobId)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.jobId = '';
      });
  }

  /**
   * Returns text for the number of updated documents.
   *
   * @param completedDocuments - Number of documents that have completed processing.
   * @param totalDocuments - Total number of documents to be processed.
   * @returns - completedDocuments of totalDocuments updated.
   */
  getCurrentUpdateText(completedDocuments: number, totalDocuments: number) {
    return this.translateService.translate('NUMBER_DOCUMENTS_PROCESSED', {
      currentDocument: completedDocuments,
      totalDocuments: totalDocuments,
    });
  }
  /**
   * Get field.
   *
   * @param id - Field Id.
   * @returns Observable<Field>.
   */
  getField(id: number) {
    return this.fieldsQuery.getField(id);
  }

  /**
   * Gets a field's form control from the set replacement form group.
   *
   * @param fieldId Field ID.
   * @returns The UntypedFormControl from the set replacement form group.
   */
  getReplacementFormControl(fieldId: number) {
    const control = this.setReplacementsFormGroup.get(fieldId.toString());
    assertExists(control, 'Control must exist.');
    this.logger.debug('List form control: ', control);
    return control as UntypedFormControl;
  }

  /**
   * Gets the backing form array for a multivalue field.
   *
   * @param fieldId Field Id.
   * @returns A form array.
   */
  getMultiValueFormArray(fieldId: number): UntypedFormArray {
    return this.setReplacementsFormGroup.get(
      fieldId.toString()
    ) as UntypedFormArray;
  }

  /**
   * Gets list observable of values.
   *
   * @param listId Id of the List.
   * @returns Observable<List>.
   */
  getListValues(listId: number) {
    return this.listsService.get(listId).pipe(map((list) => list.values));
  }

  /**
   * Translation keys for operators.
   */
  private readonly operatorTranslations: Record<SearchOperator, string> = {
    [SearchOperator.contains]: 'CONTAINS',
    [SearchOperator.doesNotEqual]: 'DOES_NOT_EQUAL',
    [SearchOperator.equals]: 'EQUALS',
    [SearchOperator.greaterThanEqual]: 'GREATER_THAN_OR_EQUAL_TO',
    [SearchOperator.lessThanEqual]: 'LESS_THAN_OR_EQUAL_TO',
    [SearchOperator.keywords]: '',
    [SearchOperator.isEmpty]: '',
    [SearchOperator.isNotEmpty]: '',
  };

  /**
   * Retrieves a search operator tooltip.
   *
   * @param searchParameter Search prompt.
   * @returns A string that should be displayed.
   */
  getSearchOperator(searchParameter: SearchParameter): string {
    const translationKey = this.operatorTranslations[searchParameter.operator];
    if (!translationKey) {
      return '';
    }
    const operatorString = this.translateService
      .translate(translationKey)
      .toLowerCase();

    return `... ${operatorString} ${this.removeTrailingColon(
      searchParameter.prompt
    )}`;
  }
  /**
   * Initalize the bulk search update.
   */
  initializeBulkSearchUpdate() {
    this.jobIsInitializing.set(true);
    this.bulkUpdateProvider
      .initializeJob(
        this.databaseId,
        this.selectedSearch.id,
        this.createPostBody()
      )
      .pipe(
        untilDestroyed(this),
        finalize(() => this.jobIsInitializing.set(false))
      )
      .subscribe({
        next: (job: BulkUpdateJob) => {
          this.totalDocuments = job.status.totalRows;
          this.jobId = job.id;
          this.logger.debug('Job: ' + job.id + ' created.');
        },
        error: (error) => {
          this.logger.error('Failed to initialize bulk update job', error);
        },
      });
  }
  /**
   * Initialize all the form groups.
   */
  initializeFormGroups() {
    this.searchSetupFormGroup = this.formBuilder.group({ search: {} });
    this.setReplacementsFormGroup = this.formBuilder.group({ fields: {} });
  }

  ngOnInit(): void {
    this.initializeFormGroups();
    if (this.dialogData) {
      this.populateDialogWithProvidedData(this.dialogData);
    }
  }

  /** Handler for the multi-value entry added event. */
  onMultiValueEntryAdded(event: MultiValueEntryAddedEvent) {
    // Use timeout to ensure underlying field component is created/initialized.
    setTimeout(() => {
      if (!event.field.systemField) {
        // Do not touch non system fields.
        return;
      }

      this.setSystemFieldControlEnabled(event.field, event.control);
    });
  }

  /** Handler for the open errored documents button click event. */
  onOpenErroredDocuments(): void {
    for (const [archiveId, documentIds] of this.errorInfo.erroredDocuments) {
      this.logger.debug(
        `Opening errored documents from archive ${archiveId}: `,
        documentIds
      );
      const searchPrompts: SearchPrompt[] = this.searchParameters.map(
        (parameter) => ({
          id: parameter.id,
          prompt: parameter.prompt,
          value: parameter.value,
        })
      );
      const navigationExtras: NavigationExtras = {
        queryParams: {
          prompts: encodeBase64(JSON.stringify(searchPrompts)),
        },
        queryParamsHandling: 'merge',
      };
      const urlTree = this.router.createUrlTree(
        [
          'db',
          this.databaseId,
          'archive',
          archiveId,
          'search',
          this.selectedSearch.id,
          'document',
          documentIds.join(),
        ],
        navigationExtras
      );

      window.open(urlTree.toString(), '_blank');
    }
  }

  /**
   * Search Selection.
   *
   */
  onSearchSelectionChange(): void {
    this.selectedSearch = this.searchSetupFormGroup.controls.search.value;
    this.searchParameters =
      this.searchSetupFormGroup.controls.search.value.parameters.map(
        (parameter: SearchParameter) => ({ ...parameter })
      );
    const search = this.searchesQuery.getSearch(this.selectedSearch.id);
    assertExists(search, 'Selected search must exist in the store.');
    this.isSearchStatic = search.parameters.every((p) => p.prompt === '');
    this.showSearchParameters = this.isSearchStatic;
  }
  /** Disables/enables fields based on overwrite system fields checkbox change. */
  overwriteSystemFieldsChange() {
    this.overwriteSystemFields = !this.overwriteSystemFields;
    this.fields$.pipe(take(1), untilDestroyed(this)).subscribe((fields) => {
      for (const field of fields) {
        if (!field.systemField) {
          // Do not touch non system fields.
          continue;
        }

        const control = this.setReplacementsFormGroup.get(field.id.toString());

        if (!control) {
          this.logger.warn(
            'Could not find form control in form group for the field.',
            field
          );
          continue;
        }

        this.setSystemFieldControlEnabled(field, control);
        if (!this.overwriteSystemFields) {
          this.clearReadOnlyMvFields();
          this.clearSystemFields();
        }
      }
    });
  }

  /** Clears the values from read only multi value fields. */
  clearReadOnlyMvFields() {
    for (const field of [...this.archiveFields, ...this.nonArchiveFields]) {
      if (field.multiValue && field.systemField !== '') {
        const control = this.setReplacementsFormGroup.get(field.id.toString());
        if (control instanceof FormArray) {
          control.controls.forEach((value, i) =>
            control.controls[i].setValue('')
          );
        }
      }
    }
  }

  /** Clear system fields. */
  clearSystemFields() {
    for (const field of [...this.archiveFields, ...this.nonArchiveFields]) {
      if (!field.multiValue && field.systemField !== '') {
        const control = this.setReplacementsFormGroup.get(field.id.toString());
        control?.setValue('');
      }
    }
  }

  /**
   * Resets dialog to a clean untouched state.
   *
   * @param stepper - MatStepper Object.
   */
  reset(stepper: MatStepper): void {
    stepper.selectedIndex = 0;
    //Need to clear values before resetting.
    for (const param of this.searchParameters) {
      param.value = '';
    }
    this.searchParameters.length = 0;
    this.showSearchParameters = false;
    this.selectedSearch = undefined as unknown as Search;
    this.searchSetupFormGroup.reset();
    this.setReplacementsFormGroup.reset();
    this.totalDocuments = 0;
    this.jobIsInitializing.set(false);
    stepper.reset();
    this.isSearchStatic = false;
  }
  /** Progress bar animation. */
  runProgressBar() {
    const sub = interval(this.pollingIntervalMS)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.bulkUpdateProvider.getJobStatus(this.jobId).subscribe({
          next: (status) => {
            this.progress = status.progress;
            this.currentDocument = status.currentRow;
            this.updateStatus(status);
            if (status.progress === 100 || status.cancelled) {
              sub.unsubscribe();
            }
          },
          error: (error) => {
            this.logger.error('Failed to get the job status:', error);
            sub.unsubscribe();
          },
        });
      });
  }

  getBulkUpdateResult() {
    this.bulkUpdateProvider
      .getJobResult(this.jobId)
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.logger.debug('Job ' + this.jobId + ' completed.', result);
        this.errorInfo.erroredNumber = result.erroredRows;
        this.errorInfo.erroredDocuments.clear();
        const errorMessages: string[] = [];
        for (const errorMessage of result.errorMessages) {
          const regex = /\{a:(\d+),d:(\d+)\}$/;
          const matches = regex.exec(errorMessage);
          if (matches) {
            const archiveId = Number(matches[1]);
            const documentId = Number(matches[2]);
            const documentIds =
              this.errorInfo.erroredDocuments.get(archiveId) ?? [];
            documentIds.push(documentId);
            this.errorInfo.erroredDocuments.set(archiveId, documentIds);
          }
          errorMessages.push(errorMessage.replace(regex, '').trim());
        }
        this.errorInfo.errorMessages = [...new Set(errorMessages)];
      });
  }
  /**
   * Run the bulk search update.
   */
  startBulkSearchUpdate() {
    this.bulkUpdateProvider
      .startUpdate(this.jobId)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.logger.debug('Job ' + this.jobId + ' started.');
        this.status = Status.updating;
        this.runProgressBar();
      });
  }
  /**
   * Update the status.
   *
   * @param status - Status response.
   */
  updateStatus(status: BulkUpdateStatus): void {
    if (this.progress === 100) {
      this.status = Status.complete;
      this.getBulkUpdateResult();
    } else if (status.cancelled) {
      this.status = Status.cancel;
    }
  }
  /**
   * Create search criteria.
   *
   * @returns  - Criteria key value pair.
   */
  private createCriteria(): { [key: number]: string } {
    const criteria: { [key: number]: string } = {};
    for (const parameter of this.searchParameters) {
      const value = parameter.value;
      if (value && value.toString().trim() !== '' && parameter.prompt?.trim()) {
        criteria[parameter.id] = value;
      }
    }
    return criteria;
  }
  /**
   * Create field update data.
   *
   * @returns - Field key value pairs.
   */
  private createUpdates(): { [key: number]: string } {
    const updates: { [key: number]: string } = {};
    for (const field of [...this.archiveFields, ...this.nonArchiveFields]) {
      if (!field.multiValue) {
        const control =
          this.setReplacementsFormGroup.controls[field.id.toString()];
        let value = control?.value;
        //DateTime is 3
        if (
          field.type === FieldDataType.date &&
          value.toString().trim() !== ''
        ) {
          value = this.getDateTime(value);
        }

        if (value && value.toString().trim() !== '') {
          updates[field.id] = value;
        }
      }
    }
    return updates;
  }

  /**
   * Updates the time so it is correct after updating.
   * @param value - DateTime string.
   * @returns - DateTime in usable format.
   */
  private getDateTime(value: string): string {
    const m = moment.parseZone(value);
    return m.hour() === 0 && m.minute() === 0 && m.second() === 0
      ? m.format('YYYY-MM-DD')
      : m.local().format('YYYY-MM-DDTHH:mm:ss');
  }

  private createMVUpdates(): { [key: number]: string[] } {
    const updates: { [key: number]: string[] } = {};
    for (const field of [...this.archiveFields, ...this.nonArchiveFields]) {
      if (field.multiValue) {
        const control =
          this.setReplacementsFormGroup.controls[field.id.toString()];
        const values: string[] = control?.value || [];
        // Filter out empty strings
        const filteredValues = values.filter(
          (v) => v !== null && v.trim() !== ''
        );

        if (filteredValues.length > 0) {
          updates[field.id] = filteredValues;
        }
      }
    }
    return updates;
  }

  private populateDialogWithProvidedData(data: BulkUpdateDialogData): void {
    if (data.search) {
      // we need to get the search from the store to ensure it is exactly the same as what will be in the mat-select
      // so that the user can see which search is being used if they choose to navigate back.
      const search = this.searchesQuery.getSearch(data.search.id);
      assertExists(search, 'Selected search must exist in the store.');
      this.searchSetupFormGroup.controls.search.setValue(search);
      this.onSearchSelectionChange(); // Ensure the same code runs as if the user had navigated to the next stepper tab.
      // In this specific instance also insert keyword search parameter
      this.searchParameters.push({
        id: -1,
        prompt: 'Keyword:',
        value: '',
        fieldId: 0,
        operator: SearchOperator.keywords,
      });
      this.setSearchPrompts(data.search.parameters); // Set search prompts using data provided to the dialog.
      this.createFieldLists();
      const stepper = this.stepper();
      assertExists(stepper, 'Stepper must exist.');
      stepper.selectedIndex = 1; // Skip to the field update step.
    }
  }
  /**
   * Retrieve prompt string without ending ':'.
   *
   * @param promptString Prompt string.
   * @returns Prompt string without ending ':'.
   */
  private removeTrailingColon(promptString: string): string {
    return promptString.replace(/:$/, '');
  }

  private setSearchPrompts(prompts: SearchPrompt[]) {
    for (const parameter of prompts) {
      this.searchSetupFormGroup.addControl(
        'Parameter' + parameter.id,
        this.formBuilder.control(parameter.value)
      );
      const targetParam = this.searchParameters.find(
        (x) => x.id === parameter.id
      );
      if (targetParam) {
        targetParam.value = parameter.value;
      }
    }
    this.createLists();
    this.showSearchParameters = true;
  }

  /**
   * Sets the provided control's state to enabled or disabled depending on the field definition and overwrite system fields state.
   *
   * @param field Field.
   * @param control Form control.
   */
  private setSystemFieldControlEnabled(field: Field, control: AbstractControl) {
    if (
      (field.systemField !== '' && !this.overwriteSystemFields) ||
      field.systemField === 'lastModified'
    ) {
      control.disable();
    } else {
      control.enable();
    }
  }
}
